import { Injectable } from '@angular/core';
import { TutorialStateService } from './tutorial-state.service';
import { TutorialActionButton } from '../models/tutorial-action.model';

/**
 * Tutorial configuration step
 */
export interface TutorialStep {
  stepNumber: number;
  title: string;
  description: string;
  imageUrl?: string;
  content?: string;
  actionButton?: TutorialActionButton; // Interactive action for this step
}

/**
 * Tutorial configuration metadata
 */
export interface TutorialConfig {
  id: string;
  title: string;
  description: string;
  featureGate: keyof BoardConfig | null; // which config flag enables this tutorial
  stepCount: number;
  steps: TutorialStep[];
  order: number; // priority/display order
  version: number; // for tracking content updates
}

/**
 * Tutorial model returned to components
 */
export interface Tutorial extends TutorialConfig {
  isAvailable: boolean;
  isCompleted?: boolean;
}

/**
 * Board configuration flags that control feature availability
 * 
 * ## Two-Flag System Architecture
 * 
 * This interface supports a two-flag model for feature entitlements:
 * 
 * | Flag Type | Controller | Purpose |
 * |-----------|------------|---------|
 * | `{feature}Purchased` | Super Admin | Gate - controls entitlement (can board access the feature?) |
 * | `enable{Feature}Channel` | Board Admin | Toggle - controls visibility (is feature currently enabled?) |
 * 
 * **Business Logic:** A feature can only be enabled if it is purchased first.
 * - If `phoneChannelPurchased = false`, board admin cannot set `enablePhoneChannel = true`
 * - Validation enforced in Admin Config UI (Story 4.4) and Firestore rules (Story 4.1)
 * 
 * @see docs/subsystems/feature-entitlement-system.md for complete documentation
 */
export interface BoardConfig {
  showDelivery?: boolean;
  enableInstoreChannel?: boolean;   // NEW - Walk-ins/floor traffic channel (default: true for backward compat)
  enablePhoneChannel?: boolean;
  enableOnlineChannel?: boolean;
  enableMultiChannel?: boolean;
  allowUpToTakePhone?: boolean;           // NEW - "up" position can take phone leads
  allowUpToTakeInternet?: boolean;        // NEW - "up" position can take online leads
  allowOnDeckToTakePhone?: boolean;       // NEW - "on deck" position can take phone leads
  allowOnDeckToTakeInternet?: boolean;    // NEW - "on deck" position can take online leads
  /** When true, single-channel boards display the modern card-based UI instead of legacy 5-column table */
  useModernLayout?: boolean;
  
  // ============================================================================
  // PURCHASED FLAGS (Super Admin Controlled)
  // These flags control feature entitlements - whether the board has purchased
  // access to premium features. Only super admins can modify these values.
  // ============================================================================
  
  /**
   * In-store channel purchased flag.
   * Always true for existing boards (base feature, always granted).
   * Super admin controlled - board admins cannot modify.
   */
  instoreChannelPurchased?: boolean;
  
  /**
   * Phone channel purchased flag.
   * Default false - premium feature that must be granted by super admin.
   * When false, board admin cannot enable `enablePhoneChannel`.
   * Super admin controlled - board admins cannot modify.
   */
  phoneChannelPurchased?: boolean;
  
  /**
   * Internet channel purchased flag.
   * Default false - premium feature that must be granted by super admin.
   * When false, board admin cannot enable `enableOnlineChannel`.
   * Super admin controlled - board admins cannot modify.
   */
  internetChannelPurchased?: boolean;
  
  [key: string]: any; // allow for extensibility
}

/**
 * Board interface for tutorial service consumption
 */
export interface BoardWithConfig {
  config?: BoardConfig;
  [key: string]: any;
}

/**
 * TUTORIAL_CONFIGURATIONS: Static configuration for all available tutorials
 * 
 * These tutorials are feature-gated by board configuration flags.
 * When a feature is disabled, the corresponding tutorial will not be available.
 * 
 * To add a new tutorial:
 * 1. Add a new object to this array with id, title, description
 * 2. Set featureGate to the BoardConfig flag that controls this tutorial (or null if always available)
 * 3. Create TutorialStep objects with stepNumber, title, description, and content
 * 4. Set stepCount to match the number of steps
 * 5. Use order to control display priority (lower numbers appear first)
 * 6. Increment version when updating tutorial content
 * 
 * See docs/guides/how-to-add-tutorials.md for detailed instructions on creating new tutorials.
 * 
 * Example tutorial configuration:
 * {
 *   id: 'my-feature',
 *   title: 'My Feature',
 *   description: 'Learn how to use My Feature',
 *   featureGate: 'enableMyFeature',
 *   stepCount: 3,
 *   steps: [ 
 *     { stepNumber: 1, title: '...', description: '...', content: '...' },
 *     { stepNumber: 2, title: '...', description: '...', content: '...' },
 *     { stepNumber: 3, title: '...', description: '...', content: '...' }
 *   ],
 *   order: 40,
 *   version: 1
 * }
 */
export const TUTORIAL_CONFIGURATIONS: TutorialConfig[] = [
  {
    id: 'delivery',
    title: 'New Feature Available',
    description: 'Announcement about the delivery visibility toggle feature',
    featureGate: 'showDelivery',
    stepCount: 1,
    steps: [
      {
        stepNumber: 1,
        title: 'You can now hide Deliveries!',
        description: 'New feature announcement',
        content: `<p>You can now control the visibility of the Deliveries section on your board. If you don't use delivery tracking, you can hide it to keep your board clean and focused.</p>`,
        actionButton: {
          action: {
            type: 'navigate' as any, // NavigateAction
            label: 'Show Me',
            route: 'admin/config',
            queryParams: { highlight: 'showDelivery' }
          },
          requiredPermission: 'admin' as any, // TutorialActionPermission.ADMIN
          buttonClass: 'btn-primary',
          icon: 'faArrowRight',
          isPrimary: true,
          tooltip: 'Navigate to Board Admin settings to see the delivery visibility option'
        }
      }
    ],
    order: 10,
    version: 3
  }
];

@Injectable({
  providedIn: 'root'
})
export class TutorialService {

  constructor(private tutorialStateService: TutorialStateService) { }

  /**
   * Get all tutorials applicable to a board based on its feature flags
   * 
   * @param board - The board object with config flags
   * @returns Array of available tutorials sorted by display order
   */
  getTutorialsForBoard(board: BoardWithConfig): Tutorial[] {
    // Filter tutorials based on feature gates
    const availableTutorials: Tutorial[] = TUTORIAL_CONFIGURATIONS
      .filter(config => this.isTutorialAvailable(board, config.id))
      .map(config => ({
        ...config,
        isAvailable: true
      }))
      .sort((a, b) => a.order - b.order);

    return availableTutorials;
  }

  /**
   * Check if a specific tutorial is available based on board config
   * 
   * @param board - The board object with config flags
   * @param tutorialId - The ID of the tutorial to check
   * @returns true if the tutorial is available for this board, false otherwise
   */
  isTutorialAvailable(board: BoardWithConfig, tutorialId: string): boolean {
    const boardConfig = board?.config || {};
    const tutorial = TUTORIAL_CONFIGURATIONS.find(t => t.id === tutorialId);

    if (!tutorial) {
      return false; // Tutorial not found
    }

    // If no feature gate, tutorial is always available
    if (tutorial.featureGate === null) {
      return true;
    }

    // Check if the feature gate flag is enabled in board config
    // Use !== false so that undefined/missing flags are treated as enabled
    // This matches the component behavior where missing flags default to true
    const featureFlag = boardConfig[tutorial.featureGate];
    return featureFlag !== false;
  }

  /**
   * Get tutorial configuration by ID
   * 
   * @param tutorialId - The ID of the tutorial to retrieve
   * @returns The tutorial configuration or undefined if not found
   */
  getTutorialById(tutorialId: string): TutorialConfig | undefined {
    return TUTORIAL_CONFIGURATIONS.find(t => t.id === tutorialId);
  }

  /**
   * Get available tutorial IDs for a board
   * 
   * @param board - The board object with config flags
   * @returns Array of available tutorial IDs
   */
  getAvailableTutorialIds(board: BoardWithConfig): string[] {
    return this.getTutorialsForBoard(board).map(t => t.id);
  }

  /**
   * Filter tutorials by feature gate
   * Useful for admin/configuration views
   * 
   * @param featureGate - The feature gate to filter by
   * @returns Array of tutorials that use this feature gate
   */
  getTutorialsByFeatureGate(featureGate: keyof BoardConfig | null): TutorialConfig[] {
    return TUTORIAL_CONFIGURATIONS.filter(t => t.featureGate === featureGate);
  }

  /**
   * Get tutorial step by tutorial ID and step number
   * 
   * @param tutorialId - The tutorial ID
   * @param stepNumber - The step number (1-indexed)
   * @returns The tutorial step or undefined if not found
   */
  getTutorialStep(tutorialId: string, stepNumber: number): TutorialStep | undefined {
    const tutorial = this.getTutorialById(tutorialId);
    if (!tutorial) {
      return undefined;
    }
    return tutorial.steps.find(s => s.stepNumber === stepNumber);
  }

  /**
   * Filter out completed and dismissed tutorials from a list
   * 
   * @param tutorials - The list of tutorials to filter
   * @returns Tutorials that have NOT been completed or dismissed by the user
   */
  filterCompletedTutorials(tutorials: Tutorial[]): Tutorial[] {
    return tutorials.filter(tutorial => {
      const completed = this.tutorialStateService.hasTutorialBeenCompleted(tutorial.id);
      const dismissed = this.tutorialStateService.hasTutorialBeenDismissed(tutorial.id);
      return !completed && !dismissed;
    });
  }
}
